import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { businessAPI } from '../../services/apiClient';
import BButton from './ui/BButton';
import BLoading from './ui/BLoading';
import styles from './BusinessAIAccessUsage.module.css';

const toDateTimeLocalValue = (isoValue) => {
  if (!isoValue) return '';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';
  const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

const toIsoValue = (dateTimeLocalValue) => {
  if (!dateTimeLocalValue) return null;
  const parsed = new Date(dateTimeLocalValue);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDateTime = (isoValue) => {
  if (!isoValue) return 'Not set';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString();
};

const BusinessAIAccessUsage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingAccess, setSavingAccess] = useState(false);
  const [adjustingPoints, setAdjustingPoints] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [accessForm, setAccessForm] = useState({
    accessExpiresAt: '',
    autoReactivateAt: '',
    durationDays: '',
  });
  const [pointsDelta, setPointsDelta] = useState('');
  const [pointsAction, setPointsAction] = useState('MANUAL_ADJUSTMENT');

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    try {
      const response = await businessAPI.getBusinessAIManagement(businessId);
      const data = response.data;
      setSnapshot(data);
      setAccessForm({
        accessExpiresAt: toDateTimeLocalValue(data?.business?.access_expires_at),
        autoReactivateAt: toDateTimeLocalValue(data?.business?.auto_reactivate_at),
        durationDays: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load business AI management data.');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  const business = snapshot?.business || {};
  const aiPoints = snapshot?.ai_points || {};
  const aiAnalytics = snapshot?.ai_analytics || {};
  const recentUsage = snapshot?.recent_ai_usage || [];
  const admins = snapshot?.admins || [];

  const accessStatusText = useMemo(() => {
    return business?.is_active ? 'Active' : 'Deactivated';
  }, [business?.is_active]);

  const handleUpdateAccess = async () => {
    const payload = {};
    const durationDays = accessForm.durationDays ? Number(accessForm.durationDays) : null;

    if (durationDays !== null && (!Number.isInteger(durationDays) || durationDays <= 0)) {
      toast.error('Duration must be a positive whole number of days.');
      return;
    }

    if (durationDays !== null) {
      payload.access_duration_days = durationDays;
    } else {
      payload.access_expires_at = toIsoValue(accessForm.accessExpiresAt);
    }

    payload.auto_reactivate_at = toIsoValue(accessForm.autoReactivateAt);

    if (payload.access_expires_at && payload.auto_reactivate_at) {
      if (new Date(payload.auto_reactivate_at) <= new Date(payload.access_expires_at)) {
        toast.error('Reactivation time must be later than deactivation time.');
        return;
      }
    }

    setSavingAccess(true);
    try {
      await businessAPI.updateBusinessAIAccessSchedule(businessId, payload);
      toast.success('Business access timer updated.');
      await fetchSnapshot();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update access timer.');
    } finally {
      setSavingAccess(false);
    }
  };

  const handleSetActiveStatus = async (isActive) => {
    setSavingAccess(true);
    try {
      await businessAPI.updateBusinessAIAccessSchedule(businessId, { is_active: isActive });
      toast.success(isActive ? 'Business reactivated.' : 'Business deactivated.');
      await fetchSnapshot();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update business status.');
    } finally {
      setSavingAccess(false);
    }
  };

  const handleClearTimers = async () => {
    setSavingAccess(true);
    try {
      await businessAPI.updateBusinessAIAccessSchedule(businessId, { clear_timers: true });
      toast.success('Access timers cleared.');
      await fetchSnapshot();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to clear timers.');
    } finally {
      setSavingAccess(false);
    }
  };

  const handleAdjustPoints = async () => {
    const delta = Number(pointsDelta);
    if (!Number.isInteger(delta) || delta === 0) {
      toast.error('Enter a non-zero whole number for point adjustment.');
      return;
    }

    setAdjustingPoints(true);
    try {
      await businessAPI.adjustBusinessAIPoints(businessId, delta, pointsAction || 'MANUAL_ADJUSTMENT');
      toast.success(`AI points ${delta > 0 ? 'added' : 'removed'} successfully.`);
      setPointsDelta('');
      await fetchSnapshot();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to adjust AI points.');
    } finally {
      setAdjustingPoints(false);
    }
  };

  if (loading) {
    return <BLoading variant="page" label="Loading business AI access..." />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Business AI Access & Usage</h1>
          <p className={styles.subtitle}>
            {business?.name || `Business #${businessId}`} - Status: <strong>{accessStatusText}</strong>
          </p>
        </div>
        <div className={styles.headerActions}>
          <BButton variant="secondary" size="sm" onClick={() => navigate('/admin/business/manage')}>
            Back to Manage Businesses
          </BButton>
        </div>
      </div>

      <section className={styles.grid}>
        <article className={styles.card}>
          <h2 className={styles.cardTitle}>Access Lifecycle</h2>
          <p className={styles.textRow}>Deactivates business admin logins while business access is off.</p>
          <p className={styles.textRow}>Current Deactivation Time: {formatDateTime(business?.access_expires_at)}</p>
          <p className={styles.textRow}>Current Reactivation Time: {formatDateTime(business?.auto_reactivate_at)}</p>

          <div className={styles.formGrid}>
            <label className={styles.label}>
              Access Duration (days)
              <input
                className={styles.input}
                type="number"
                min="1"
                value={accessForm.durationDays}
                onChange={(e) => setAccessForm((prev) => ({ ...prev, durationDays: e.target.value }))}
                placeholder="Optional quick timer"
              />
            </label>
            <label className={styles.label}>
              Deactivate At
              <input
                className={styles.input}
                type="datetime-local"
                value={accessForm.accessExpiresAt}
                onChange={(e) => setAccessForm((prev) => ({ ...prev, accessExpiresAt: e.target.value, durationDays: '' }))}
              />
            </label>
            <label className={styles.label}>
              Reactivate At
              <input
                className={styles.input}
                type="datetime-local"
                value={accessForm.autoReactivateAt}
                onChange={(e) => setAccessForm((prev) => ({ ...prev, autoReactivateAt: e.target.value }))}
              />
            </label>
          </div>

          <div className={styles.rowActions}>
            <BButton variant="primary" size="sm" onClick={handleUpdateAccess} disabled={savingAccess}>
              Save Timer
            </BButton>
            <BButton variant="secondary" size="sm" onClick={handleClearTimers} disabled={savingAccess}>
              Clear Timers
            </BButton>
            <BButton
              variant={business?.is_active ? 'danger' : 'primary'}
              size="sm"
              onClick={() => handleSetActiveStatus(!business?.is_active)}
              disabled={savingAccess}
            >
              {business?.is_active ? 'Deactivate Now' : 'Reactivate Now'}
            </BButton>
          </div>
        </article>

        <article className={styles.card}>
          <h2 className={styles.cardTitle}>AI Points Management</h2>
          <div className={styles.stats}>
            <div className={styles.statBox}>
              <span>Total</span>
              <strong>{aiPoints?.total_ai_points ?? 0}</strong>
            </div>
            <div className={styles.statBox}>
              <span>Monthly</span>
              <strong>{aiPoints?.ai_points_monthly ?? 0}</strong>
            </div>
            <div className={styles.statBox}>
              <span>Purchased</span>
              <strong>{aiPoints?.ai_points_purchased ?? 0}</strong>
            </div>
          </div>
          <p className={styles.textRow}>Days Until Reset: {aiPoints?.days_until_reset ?? 'N/A'}</p>

          <div className={styles.formGrid}>
            <label className={styles.label}>
              Points Delta (+ add / - remove)
              <input
                className={styles.input}
                type="number"
                value={pointsDelta}
                onChange={(e) => setPointsDelta(e.target.value)}
                placeholder="e.g. 50 or -50"
              />
            </label>
            <label className={styles.label}>
              Action Label
              <input
                className={styles.input}
                type="text"
                value={pointsAction}
                onChange={(e) => setPointsAction(e.target.value)}
                placeholder="MANUAL_ADJUSTMENT"
              />
            </label>
          </div>

          <div className={styles.rowActions}>
            <BButton variant="primary" size="sm" onClick={handleAdjustPoints} disabled={adjustingPoints}>
              Apply Point Adjustment
            </BButton>
          </div>
        </article>
      </section>

      <section className={styles.analyticsSection}>
        <h2 className={styles.cardTitle}>AI Analytics</h2>
        <div className={styles.stats}>
          <div className={styles.statBox}>
            <span>AI Surveys Created</span>
            <strong>{aiAnalytics?.ai_surveys_created ?? aiAnalytics?.services_created ?? 0}</strong>
          </div>
          <div className={styles.statBox}>
            <span>Credits Used</span>
            <strong>{aiAnalytics?.credits_used ?? 0}</strong>
          </div>
          <div className={styles.statBox}>
            <span>AI Cost So Far</span>
            <strong>{formatCurrency(aiAnalytics?.total_ai_cost_usd)}</strong>
          </div>
          <div className={styles.statBox}>
            <span>Total AI Requests</span>
            <strong>{aiAnalytics?.total_ai_requests ?? 0}</strong>
          </div>
        </div>
      </section>

      <section className={styles.analyticsSection}>
        <h2 className={styles.cardTitle}>Business Admin Accounts</h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.name || '-'}</td>
                    <td>{admin.email}</td>
                    <td>{admin.is_active ? 'Active' : 'Disabled'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className={styles.emptyCell}>No business admins found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.analyticsSection}>
        <h2 className={styles.cardTitle}>Recent AI Usage</h2>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Subtype</th>
                <th>Credits</th>
                <th>Cost</th>
                <th>Status</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {recentUsage.length > 0 ? (
                recentUsage.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.operation_type || '-'}</td>
                    <td>{entry.operation_subtype || '-'}</td>
                    <td>{entry.points_cost || 0}</td>
                    <td>{formatCurrency(entry.openai_cost_usd)}</td>
                    <td>{entry.success ? 'Success' : 'Failed'}</td>
                    <td>{formatDateTime(entry.created_at)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className={styles.emptyCell}>No AI activity found yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
};

export default BusinessAIAccessUsage;
